import React from "react";
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import { useUpsellDashboard } from "../../upselling/hooks/useUpsellDashboard";
import { useUpsellCardSelection } from "../../upselling/hooks/useUpsellCardSelection";
import { useUpsellMetrics } from "../../upselling/hooks/useUpsellMetrics";
import { BOOKING_PERIOD_OPTIONS } from "../../upselling/constants/dashboardConstants";
import { RenderUpsellServiceVenueCard } from "../../upselling/components/RenderUpsellServiceVenueCard";
import { NoRecordFound } from "../../../components/NoRecordFound/NoRecordFound";
import ConfirmationCampaign from "../../upselling/components/dashboard/ConfirmationCampaign";
import LoadingDialogWithMessage from "../../../components/Dialogs/LoadingDialogWithMessage";
import { UpsellLeadsRightPanel } from "../../upselling/components/UpsellLeadsRightPanel";
import {
  NoBookedLeadsWarning,
  TopPerformers,
  UnableToFetchInsightsHero,
  VenuePerformanceDonutGraph,
  ServiceCategoriesDonutGraph,
  FoodPerformanceDonutGraph,
} from "../../upselling/components";
import { MobileDashboardSection } from "./MobileDashboardSection";

export const MobileUpsellContent: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    dashboardData,
    showUnableUpsell,
    showWarning,
    selectedBookingPeriod,
    selectedServiceCategory,
    selectedVenueCategory,
    selectedFoodCategory,
    confirmDialogOpen,
    campaignLoading,
    serviceCardsWithUpsells,
    venueCardsWithUpsells,
    foodCardsWithUpsells,
    setSelectedServiceCategory,
    setSelectedVenueCategory,
    setSelectedFoodCategory,
    setConfirmDialogOpen,
    handleBookingPeriodSelect,
    confirmCampaignGeneration,
    initiateCampaignGeneration,
    navigate,
  } = useUpsellDashboard();

  const {
    selectedCount,
    handleCardCheckChange,
    isCardSelected,
    getSelectedUuids,
    getSelectedCardNames,
    clearSelection,
    selectAll,
  } = useUpsellCardSelection();

  const metrics = useUpsellMetrics(dashboardData);

  const handleBulkAutoGenerateCampaign = () => {
    const selectedUuids = getSelectedUuids();
    initiateCampaignGeneration(selectedUuids, selectedCount);
  };

  const totalCardsCount =
    (venueCardsWithUpsells?.length || 0) +
    (serviceCardsWithUpsells?.length || 0) +
    (foodCardsWithUpsells?.length || 0);

  const allCardsSelected =
    totalCardsCount > 0 && selectedCount === totalCardsCount;

  const handleSelectAllCards = () => {
    if (allCardsSelected) {
      clearSelection();
      return;
    }

    const allCards: {
      uuid: string;
      type: "venue" | "service" | "food";
      name?: string;
    }[] = [];

    if (venueCardsWithUpsells && venueCardsWithUpsells.length > 0) {
      venueCardsWithUpsells.forEach((item: any) => {
        if (item.venue_uuid) {
          allCards.push({
            uuid: item.venue_uuid,
            type: "venue",
            name: item.venue_name || "",
          });
        }
      });
    }

    if (serviceCardsWithUpsells && serviceCardsWithUpsells.length > 0) {
      serviceCardsWithUpsells.forEach((item: any) => {
        if (item.service_category_uuid) {
          allCards.push({
            uuid: item.service_category_uuid,
            type: "service",
            name: item.service_category_name || "",
          });
        }
      });
    }

    if (foodCardsWithUpsells && foodCardsWithUpsells.length > 0) {
      foodCardsWithUpsells.forEach((item: any) => {
        if (item.food_item_uuid) {
          allCards.push({
            uuid: item.food_item_uuid,
            type: "food",
            name: item.item_name || "",
          });
        }
      });
    }

    selectAll(allCards);
  };

  const handleConfirmCampaign = async () => {
    const success = await confirmCampaignGeneration();
    if (success) {
      clearSelection();
    }
  };

  if (showUnableUpsell) {
    return (
      <Box sx={{ width: "100%", pt: 1 }}>
        <UnableToFetchInsightsHero />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      {/* Warning banner if applicable */}
      {showWarning && <NoBookedLeadsWarning />}

      {/* Booking Period Selector Chips */}
      <MobileDashboardSection
        title={
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: "1.3rem" }}>
            Booking Period
          </Typography>
        }
        disableCard
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
            width: "100%",
          }}
        >
          {BOOKING_PERIOD_OPTIONS.map((period) => {
            const isSelected = selectedBookingPeriod === period.value;
            return (
              <ButtonBase
                key={period.value}
                onClick={() => handleBookingPeriodSelect(period.value)}
                sx={{
                  py: 1,
                  px: 1,
                  borderRadius: "14px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 52,
                  transition: "all 0.2s ease",
                  backgroundColor: isSelected
                    ? theme.palette.primary.main
                    : isDark
                      ? "rgba(30, 41, 59, 0.85)"
                      : "#ffffff",
                  color: isSelected ? "#ffffff" : theme.palette.text.primary,
                  border: `1px solid ${
                    isSelected
                      ? theme.palette.primary.main
                      : isDark
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)"
                  }`,
                  boxShadow: isSelected
                    ? "0 4px 14px rgba(26, 151, 245, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.04)",
                  "&:active": {
                    transform: "scale(0.96)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textAlign: "center",
                  }}
                >
                  {period.label}
                </Typography>
                {period.subLabel && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      opacity: isSelected ? 0.85 : 0.6,
                      lineHeight: 1.1,
                      mt: 0.25,
                      textAlign: "center",
                    }}
                  >
                    {period.subLabel}
                  </Typography>
                )}
              </ButtonBase>
            );
          })}
        </Box>
      </MobileDashboardSection>

      {/* Action Buttons: Select All + Auto Generate Campaign */}
      <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
        <Button
          fullWidth
          variant={allCardsSelected ? "outlined" : "contained"}
          color="inherit"
          onClick={handleSelectAllCards}
          disabled={
            !venueCardsWithUpsells?.length &&
            !serviceCardsWithUpsells?.length &&
            !foodCardsWithUpsells?.length
          }
          sx={{
            py: 1.25,
            borderRadius: "14px",
            fontWeight: 700,
            fontSize: "0.82rem",
            textTransform: "none",
            backgroundColor: allCardsSelected
              ? "transparent"
              : isDark
                ? "rgba(255,255,255,0.1)"
                : "#f1f5f9",
            color: isDark ? "#ffffff" : "#334155",
            border: `1px solid ${
              isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"
            }`,
          }}
        >
          {allCardsSelected ? "Deselect All" : "Select All Cards"}
        </Button>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<WhatsAppIcon fontSize="small" />}
          onClick={handleBulkAutoGenerateCampaign}
          disabled={selectedCount === 0}
          sx={{
            py: 1.25,
            borderRadius: "14px",
            fontWeight: 700,
            fontSize: "0.82rem",
            textTransform: "none",
            backgroundColor: "#25D366",
            "&:hover": { backgroundColor: "#1da851" },
            boxShadow: "0 4px 14px rgba(37, 211, 102, 0.3)",
          }}
        >
          {selectedCount > 0 ? `Campaign (${selectedCount})` : "Campaign"}
        </Button>
      </Stack>

      {/* If dashboardData is empty, show ONE single clean fallback message */}
      {dashboardData && dashboardData.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            py: 3,
            px: 2,
            backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
            borderRadius: "16px",
            border: `1px solid ${
              isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"
            }`,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          <NoRecordFound
            title="No Upsell Opportunities Found"
            description="Try selecting a different booking period to view opportunities and analytics"
            showAddButton={false}
            showRefreshButton={false}
            showFilterButton={false}
            height="180px"
          />
        </Box>
      ) : (
        <>
          {/* Upsell Cards Section */}
          <MobileDashboardSection
            title={
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, fontSize: "1.3rem" }}
              >
                Recent Opportunities
              </Typography>
            }
            disableCard
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Venue Cards */}
              {venueCardsWithUpsells?.map((item: any, index: number) => (
                <RenderUpsellServiceVenueCard
                  key={`venue-${index}`}
                  venue={item}
                  isFromUpsellDashboard
                  isSelected={isCardSelected(item.venue_uuid)}
                  upsellData={item.uniqueUpsells}
                  isSelectable
                  isChecked={isCardSelected(item.venue_uuid)}
                  onCheckChange={handleCardCheckChange}
                  isMobileCompact
                  onViewLeads={() => {
                    if ((item.leads?.length ?? 0) > 0) {
                      setSelectedVenueCategory(item);
                    }
                  }}
                />
              ))}

              {/* Service Cards */}
              {serviceCardsWithUpsells?.map((item: any, index: number) => (
                <RenderUpsellServiceVenueCard
                  key={`service-${index}`}
                  service={item}
                  isFromUpsellDashboard
                  isSelected={isCardSelected(item.service_category_uuid)}
                  upsellData={item.uniqueUpsells}
                  isSelectable
                  isChecked={isCardSelected(item.service_category_uuid)}
                  onCheckChange={handleCardCheckChange}
                  isMobileCompact
                  onViewLeads={() => {
                    if ((item.leads?.length ?? 0) > 0) {
                      setSelectedServiceCategory(item);
                    }
                  }}
                />
              ))}

              {/* Food Cards */}
              {foodCardsWithUpsells?.map((item: any, index: number) => (
                <RenderUpsellServiceVenueCard
                  key={`food-${index}`}
                  food={item}
                  isFromUpsellDashboard
                  isSelected={isCardSelected(item.food_item_uuid)}
                  upsellData={item.uniqueUpsells}
                  isSelectable
                  isChecked={isCardSelected(item.food_item_uuid)}
                  onCheckChange={handleCardCheckChange}
                  isMobileCompact
                  onViewLeads={() => {
                    if ((item.leads?.length ?? 0) > 0) {
                      setSelectedFoodCategory(item);
                    }
                  }}
                />
              ))}
            </Box>
          </MobileDashboardSection>

          {/* Analytics Overview Graphs */}
          <MobileDashboardSection
            title={
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, fontSize: "1.3rem" }}
              >
                Analytics Overview
              </Typography>
            }
            disableCard
          >
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <ServiceCategoriesDonutGraph
                  dashboardData={dashboardData}
                  height={300}
                />
              </Box>
              <Box sx={{ width: "100%" }}>
                <VenuePerformanceDonutGraph
                  dashboardData={dashboardData}
                  height={300}
                />
              </Box>
              <Box sx={{ width: "100%" }}>
                <FoodPerformanceDonutGraph
                  dashboardData={dashboardData}
                  height={300}
                />
              </Box>
            </Stack>
          </MobileDashboardSection>

          {/* Top Performers */}
          <Box sx={{ width: "100%" }}>
            <TopPerformers dashboardData={dashboardData} metrics={metrics} />
          </Box>
        </>
      )}

      {/* Confirmation Dialog for Auto Generate Campaign */}
      <ConfirmationCampaign
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        selectedCards={getSelectedCardNames()}
        onConfirm={handleConfirmCampaign}
      />

      {/* Loading Dialog for Campaign Generation */}
      {campaignLoading && (
        <LoadingDialogWithMessage
          open={campaignLoading}
          label="Generating campaign..."
        />
      )}

      {/* Category Leads Right Panels */}
      {selectedVenueCategory && (
        <UpsellLeadsRightPanel
          open={Boolean(selectedVenueCategory)}
          onClose={() => setSelectedVenueCategory(null)}
          selectedVenueCategory={selectedVenueCategory}
          setSelectedVenueCategory={setSelectedVenueCategory}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}

      {selectedServiceCategory && (
        <UpsellLeadsRightPanel
          open={Boolean(selectedServiceCategory)}
          onClose={() => setSelectedServiceCategory(null)}
          selectedVenueCategory={selectedServiceCategory}
          setSelectedVenueCategory={setSelectedServiceCategory}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}

      {selectedFoodCategory && (
        <UpsellLeadsRightPanel
          open={Boolean(selectedFoodCategory)}
          onClose={() => setSelectedFoodCategory(null)}
          selectedVenueCategory={selectedFoodCategory}
          setSelectedVenueCategory={setSelectedFoodCategory}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}
    </Stack>
  );
};
